import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  ClockInIcon,
  ClockOutIcon,
  CONTENT_ID_SELECTOR,
  Heading,
  Inscription,
  Preloader,
  SetupOutlinedIcon,
  Tip,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import Permissions from '@/common/permissions';
import routes from '@/common/routes';
import FixedTopWrapper from '@/modules/core/components/FixedTopWrapper';
import Topbar from '@/modules/core/components/Topbar/Topbar';
import { QueryKeys, useHasAccess, useHistory } from '@/modules/core/hooks';
import { useContactInfoQuery } from '@/modules/user/queries';
import { useAuthStore } from '@/modules/user/stores';
import {
  CLAIMS_PROCESSORS_MANAGEMENT_REQUIREMENTS,
  useHasAccessForObservingOrganization,
} from '@/pages/ManageClaimsProcessors.page';
import globalTheme from '@/styles/theme';
import theme from '@/styles/theme';

import { setExtraKey } from './queries/useClaimsList.query';
import useClockInQuery from './queries/useClockIn.query';
import AppealClaimsList from './AppealClaimsList';
import ClaimsFilterSection, { CLAIM_APPEAL_REQUIREMENTS, PageMode } from './ClaimsFilterSection';
import ClaimsList, { EXTENDED_HEADER_CLAIMS_SPACING, HEADER_CLAIMS_SPACING } from './ClaimsList';
import EscalatedClaimsList from './EscalatedClaimsList';

const REFETCH_INTERVAL = 5000;

const Wrapper = styled(Box)`
  > div:not(:first-child) {
    margin-left: ${({ theme }) => theme.spacings.spacing12};
  }
`;

const Claims = () => {
  const hasAccessToAssignClaim = useHasAccess([{ permission: Permissions.CLAIM_TEAM }]);
  const hasAccessForObservingOrganization = useHasAccessForObservingOrganization();
  const hasAccessToAppeals = useHasAccess(CLAIM_APPEAL_REQUIREMENTS);
  const showAppealsTab = hasAccessToAppeals;
  const { contactId } = useAuthStore();
  const { formattedData, isLoading, refetch, isRefetching } = useContactInfoQuery(contactId);
  const [isClockIn, setIsClockIn] = useState(false);
  const updateClockIn = useClockInQuery();
  const [isClockInLoading, setIsClockInLoading] = useState(false);
  const isClockInProcessing = useMemo(
    () => formattedData?.clockIn !== formattedData?.claimsAssigned,
    [formattedData]
  );
  const isClockInDataLoading = useMemo(
    () => isClockInLoading || isLoading || isClockInProcessing,
    [isClockInLoading, isLoading, isClockInProcessing]
  );
  const refetchContactInfo = useCallback(() => {
    if (isClockInProcessing) {
      refetch();
    }
  }, [refetch, isClockInProcessing]);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!isRefetching && isClockInProcessing) {
      timer = setTimeout(() => {
        refetchContactInfo();
      }, REFETCH_INTERVAL);
    }

    return () => clearTimeout(timer);
  }, [isRefetching, isClockInProcessing, refetchContactInfo]);
  const handleUpdateClockIn = useCallback(async () => {
    setIsClockInLoading(true);
    await updateClockIn(!isClockIn);
  }, [isClockIn, updateClockIn]);
  useEffect(() => {
    if (formattedData.clockIn !== isClockIn) setIsClockIn(formattedData.clockIn);
  }, [formattedData, isClockIn, setIsClockIn]);
  useEffect(() => setIsClockInLoading(false), [isClockIn]);

  const hasAccessToClockIn = useHasAccess([{ permission: Permissions.CLOCK_IN }]);
  const hasViewClaim = useHasAccess([{ permission: Permissions.VIEW_CLAIM }]);
  const showClockInFeatures = hasAccessToClockIn;
  const getDefaultPageMode = () => {
    if (showClockInFeatures) return PageMode.MY;
    if (!hasViewClaim && showAppealsTab) return PageMode.APPEALS;
    return PageMode.ALL;
  };
  const defaultPageMode = getDefaultPageMode();
  const [pageMode, setPageMode] = useState(defaultPageMode);

  const [totalAll, setTotalAll] = useState<number | undefined>();
  const [totalMy, setTotalMy] = useState<number | undefined>();
  const [totalEscalated, setTotalEscalated] = useState<number | undefined>();
  const [totalAppeals, setTotalAppeals] = useState<number | undefined>();

  const [isOnlyMyAssignments, isShowOnlyEscalated, isAllClaims, isAppealsTab] = useMemo(
    () => [
      pageMode === PageMode.MY,
      pageMode === PageMode.ESCALATED,
      pageMode === PageMode.ALL,
      pageMode === PageMode.APPEALS,
    ],
    [pageMode]
  );
  useEffect(() => {
    if (isAppealsTab) return;
    setExtraKey({ isOnlyMyAssignments, isShowOnlyEscalated });
  }, [isOnlyMyAssignments, isShowOnlyEscalated, isAppealsTab]);

  const hasAccessToManageClaimsProcessors = useHasAccess(CLAIMS_PROCESSORS_MANAGEMENT_REQUIREMENTS);
  const [isScrolled, setIsScrolled] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const [isEmptyClaimList, setIsEmptyClaimList] = useState(false);
  const [isPageMounted, setIsPageMounted] = useState(false);

  useEffect(() => {
    const emptyClaimListQuery = new URLSearchParams(location.search)?.get(QueryKeys.EMPTY_CLAIMS_LIST);
    const searchParams = new URLSearchParams(location.search);

    if (emptyClaimListQuery) {
      setIsEmptyClaimList(true);
    }
    setIsPageMounted(true);

    const activePageMode = searchParams?.get('activePageMode');

    if (
      activePageMode &&
      (activePageMode === PageMode.ALL ||
        activePageMode === PageMode.ESCALATED ||
        activePageMode === PageMode.MY ||
        activePageMode === PageMode.APPEALS)
    ) {
      setPageMode(activePageMode as PageMode);
    }

    if (searchParams?.get(`search${PageMode.ALL}`) || emptyClaimListQuery) {
      setPageMode(PageMode.ALL);
    } else if (searchParams?.get(`search${PageMode.ESCALATED}`)) {
      setPageMode(PageMode.ESCALATED);
    } else if (searchParams?.get(`search${PageMode.MY}`)) {
      setPageMode(PageMode.MY);
    } else if (searchParams?.get(`search${PageMode.APPEALS}`)) {
      setPageMode(PageMode.APPEALS);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const appContainer = document.getElementById(CONTENT_ID_SELECTOR);
    const updateScrolling = () =>
      setIsScrolled(
        !!appContainer &&
          appContainer.scrollTop >
            (hasAccessToAssignClaim ? EXTENDED_HEADER_CLAIMS_SPACING : HEADER_CLAIMS_SPACING) / 2
      );

    appContainer?.addEventListener('scroll', updateScrolling);

    return () => {
      setIsScrolled(false);
      appContainer?.removeEventListener('scroll', updateScrolling);
    };
  }, [hasAccessToAssignClaim]);

  const goToClaimsProcessorsPage = useCallback(
    () => history.push(routes.MANAGE_CLAIMS_PROCESSORS),
    [history]
  );

  return (
    <Box margin={{ bottom: '72px' }} align="center" width="100%" height="fit-content">
      <FixedTopWrapper>
        <Topbar hideHeader={isScrolled} />
        <Box
          width={globalTheme.defaultContentWidth}
          alignSelf="center"
          direction="row"
          justify="between"
          align="center"
          wrap
          height="54px"
          {...(isScrolled
            ? {}
            : {
                margin: { top: 'spacing12' },
              })}
        >
          <Box direction="row" width="100%" align="center">
            <Heading level={isScrolled ? 2 : 1} size="regular" color="textTitle">
              {isAppealsTab ? 'Appeals' : 'Claims'}
            </Heading>

            <Box flex direction="row" justify="end" align="center">
              {showClockInFeatures || showAppealsTab ? (
                <Box align="center" direction="row">
                  <ClaimsFilterSection
                    totalAll={isEmptyClaimList ? 0 : totalAll}
                    totalMy={totalMy}
                    totalEscalated={totalEscalated}
                    totalAppeals={totalAppeals}
                    setPageMode={setPageMode}
                    mode={pageMode}
                    hasAccessToMyAssignments={showClockInFeatures}
                  />
                </Box>
              ) : null}

              {hasAccessToManageClaimsProcessors || showClockInFeatures ? (
                <Box
                  direction="row"
                  justify="end"
                  align="center"
                  height="40px"
                  margin={{ left: 'spacing24' }}
                >
                  {(hasAccessToManageClaimsProcessors && hasAccessForObservingOrganization) ||
                  showClockInFeatures ? (
                    <Wrapper direction="row">
                      {hasAccessToManageClaimsProcessors && hasAccessForObservingOrganization ? (
                        <Box
                          width="40px"
                          height="40px"
                          round="button1Round"
                          justify="center"
                          align="center"
                          style={{
                            border: `2px solid ${theme.colors.border}`,
                          }}
                          onClick={goToClaimsProcessorsPage}
                        >
                          <SetupOutlinedIcon color="iconPrimary" />
                        </Box>
                      ) : null}

                      {showClockInFeatures ? (
                        <Tip
                          plain
                          dropProps={{ align: { top: 'bottom' } }}
                          content={
                            <Box pad={{ left: 'spacing12' }}>
                              <Box
                                width="max-content"
                                height={{ max: 'l' }}
                                align="center"
                                justify="center"
                                background="background3"
                                border={{ size: 'small', color: 'popup' }}
                                pad={{ horizontal: 'spacing6' }}
                                margin={{ top: 'spacing16' }}
                              >
                                <Inscription size="small" color="textOnColor">
                                  {isClockIn ? 'Clock out' : 'Clock in'}
                                </Inscription>
                              </Box>
                            </Box>
                          }
                        >
                          <Box
                            width="40px"
                            height="40px"
                            round="button1Round"
                            justify="center"
                            align="center"
                            background={isClockIn ? 'buttonPrimary' : 'info'}
                            {...(isClockInDataLoading ? {} : { onClick: handleUpdateClockIn })}
                          >
                            <Box
                              width="24px"
                              direction="row"
                              height="24px"
                              round
                              align="center"
                              justify="center"
                            >
                              {isClockIn && !isClockInDataLoading ? <ClockOutIcon color="canvas" /> : null}
                              {!isClockIn && !isClockInDataLoading ? <ClockInIcon color="canvas" /> : null}
                              {isClockInDataLoading ? <Preloader size={12} /> : null}
                            </Box>
                          </Box>
                        </Tip>
                      ) : null}
                    </Wrapper>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
        {isScrolled ? (
          <Box width="100%" border={{ color: 'border1', side: 'bottom', size: 'small' }} />
        ) : null}
      </FixedTopWrapper>

      {isPageMounted ? (
        <Box>
          {showClockInFeatures ? (
            <Box key="OnlyMyAssignments" {...(isOnlyMyAssignments ? {} : { style: { display: 'none' } })}>
              <ClaimsList
                isOnlyMyAssignments
                hasAccessToAssignClaim={hasAccessToAssignClaim}
                setTotal={setTotalMy}
                isClockIn={isClockIn}
                hasAccessToClockIn={hasAccessToClockIn}
                pageMode={PageMode.MY}
                disabled={!isOnlyMyAssignments}
              />
            </Box>
          ) : null}

          {showClockInFeatures || showAppealsTab ? (
            <Box key="ExtendedMode" {...(isShowOnlyEscalated ? {} : { style: { display: 'none' } })}>
              <EscalatedClaimsList
                hasAccessToAssignClaim={hasAccessToAssignClaim}
                setTotal={setTotalEscalated}
                pageMode={PageMode.ESCALATED}
                disabled={!isShowOnlyEscalated}
              />
            </Box>
          ) : null}

          <Box key="All" {...(isAllClaims ? {} : { style: { display: 'none' } })}>
            <ClaimsList
              hasAccessToAssignClaim={hasAccessToAssignClaim}
              setTotal={setTotalAll}
              pageMode={PageMode.ALL}
              disabled={!isAllClaims}
            />
          </Box>

          {showAppealsTab ? (
            <Box key="Appeals" {...(isAppealsTab ? {} : { style: { display: 'none' } })}>
              <AppealClaimsList
                hasAccessToAssignClaim={hasAccessToAssignClaim}
                setTotal={setTotalAppeals}
                pageMode={PageMode.APPEALS}
                disabled={!isAppealsTab}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default Claims;
